import React from 'react';
import ActionButton from '../../components/ui/ActionButton';
import DataTable, { Column } from '../../components/ui/DataTable';
import {
  StatusBadge,
  selectStyle,
  labelStyle,
} from './types';

interface DataUploadTabProps {
  entities: any[];
  // Entity master upload
  entityFile: File | null;
  setEntityFile: (f: File | null) => void;
  entityUploading: boolean;
  entityResult: any;
  entityInputRef: React.RefObject<HTMLInputElement | null>;
  handleEntityUpload: () => void;
  // Data upload form
  dataEntity: string;
  setDataEntity: (v: string) => void;
  dataType: string;
  setDataType: (v: string) => void;
  erpType: string;
  setErpType: (v: string) => void;
  dataFile: File | null;
  setDataFile: (f: File | null) => void;
  dataUploading: boolean;
  dataDragOver: boolean;
  setDataDragOver: (v: boolean) => void;
  dataInputRef: React.RefObject<HTMLInputElement | null>;
  handleDataUpload: () => void;
  handleDrag: (e: React.DragEvent, setOver: (v: boolean) => void, over: boolean) => void;
  handleDrop: (e: React.DragEvent, setFile: (f: File | null) => void, setOver: (v: boolean) => void) => void;
  // Active processing
  activeDataUploadId: number | null;
  // Upload history
  dataUploads: any[];
  dataUploadsLoading: boolean;
}

export default function DataUploadTab({
  entities,
  entityFile,
  setEntityFile,
  entityUploading,
  entityResult,
  entityInputRef,
  handleEntityUpload,
  dataEntity,
  setDataEntity,
  dataType,
  setDataType,
  erpType,
  setErpType,
  dataFile,
  setDataFile,
  dataUploading,
  dataDragOver,
  setDataDragOver,
  dataInputRef,
  handleDataUpload,
  handleDrag,
  handleDrop,
  activeDataUploadId,
  dataUploads,
  dataUploadsLoading,
}: DataUploadTabProps) {

  const historyColumns: Column[] = [
    { key: 'uploaded_at', label: 'Date', render: (v: any) => v ? <span style={{ whiteSpace: 'nowrap', fontSize: 11 }}>{new Date(v).toLocaleString()}</span> : '-' },
    { key: 'entity_code', label: 'Entity', render: (v: any) => <span style={{ fontWeight: 600 }}>{v || '-'}</span> },
    { key: 'data_type', label: 'Data Type', render: (v: any) => (
      <span style={{ padding: '2px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600, background: 'var(--accent-subtle)', color: 'var(--accent)' }}>
        {v || '-'}
      </span>
    )},
    { key: 'erp_type', label: 'ERP', render: (v: any) => <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{v || '-'}</span> },
    { key: 'filename', label: 'File', render: (v: any) => <span style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }}>{v}</span> },
    { key: 'status', label: 'Status', render: (v: any) => <StatusBadge status={v} /> },
    { key: 'rows_ingested', label: 'Rows', align: 'right', render: (v: any) => <span style={{ fontFamily: 'var(--font-mono)' }}>{v ?? '-'}</span> },
    { key: 'rows_rejected', label: 'Rejected', align: 'right', render: (v: any) => <span style={{ fontFamily: 'var(--font-mono)', color: (v || 0) > 0 ? '#fca5a5' : 'var(--text-tertiary)' }}>{v ?? '-'}</span> },
    { key: 'processing_time_ms', label: 'Time', align: 'right', render: (v: any) => (
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>
        {v == null ? '\u2014' : v < 1000 ? `${v}ms` : `${(v / 1000).toFixed(1)}s`}
      </span>
    )},
    { key: 'uploaded_by', label: 'By', render: (v: any) => <span style={{ fontSize: 11, color: 'var(--text-tertiary)', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block' }}>{v}</span> },
  ];

  return (
    <>
      {/* Entity Master Upload */}
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border-default)',
        borderRadius: 8, padding: 16, marginBottom: 20,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: entityFile || entityResult ? 12 : 0 }}>
          <div>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Entity Master</span>
            <span style={{ fontSize: 10, color: 'var(--text-tertiary)', marginLeft: 8 }}>
              CSV with entity_code, entity_name, country, functional_currency, etc.
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              ref={entityInputRef}
              type="file"
              accept=".csv"
              onChange={e => setEntityFile(e.target.files?.[0] || null)}
              style={{ display: 'none' }}
            />
            <ActionButton
              label="Choose CSV"
              onClick={() => entityInputRef.current?.click()}
              variant="secondary"
              size="sm"
            />
            {entityFile && (
              <>
                <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                  {entityFile.name} ({(entityFile.size / 1024).toFixed(1)} KB)
                </span>
                <ActionButton
                  label={entityUploading ? 'Uploading...' : 'Upload Entities'}
                  onClick={() => handleEntityUpload()}
                  variant="primary"
                  size="sm"
                  disabled={entityUploading}
                />
                <span
                  style={{ fontSize: 11, color: '#ef4444', cursor: 'pointer' }}
                  onClick={() => setEntityFile(null)}
                >
                  Remove
                </span>
              </>
            )}
          </div>
        </div>
        {entityResult && (
          <div style={{
            marginTop: 8, padding: 10, borderRadius: 6, fontSize: 11,
            background: entityResult.status === 'error'
              ? 'rgba(239,68,68,0.08)' : 'rgba(34,197,94,0.08)',
            border: `1px solid ${entityResult.status === 'error'
              ? 'rgba(239,68,68,0.2)' : 'rgba(34,197,94,0.2)'}`,
            color: entityResult.status === 'error' ? '#fca5a5' : 'var(--positive)',
          }}>
            {entityResult.status === 'error' ? (
              <span>Error: {entityResult.error}</span>
            ) : (
              <span>
                {entityResult.created} created, {entityResult.updated} updated
                {entityResult.errors?.length > 0 && (
                  <span style={{ color: 'var(--warning)', marginLeft: 8 }}>
                    ({entityResult.errors.length} errors)
                  </span>
                )}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Upload form */}
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border-default)',
        borderRadius: 8, padding: 20, marginBottom: 20,
      }}>
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={labelStyle}>Entity (fallback)</label>
            <select value={dataEntity} onChange={e => setDataEntity(e.target.value)} style={selectStyle}>
              <option value="">Auto from row data</option>
              {entities.map((ent: any) => (
                <option key={ent.entity_code} value={ent.entity_code}>
                  {ent.entity_code} - {ent.entity_name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Data Type</label>
            <select value={dataType} onChange={e => setDataType(e.target.value)} style={selectStyle}>
              <option value="AUTO">Auto-detect</option>
              <option value="AP_INVOICE">AP Invoices</option>
              <option value="PURCHASE_ORDER">Purchase Orders</option>
              <option value="GOODS_RECEIPT">Goods Receipts</option>
              <option value="VENDOR">Vendors</option>
              <option value="CUSTOMER">Customers</option>
              <option value="ASSET">Fixed Assets</option>
              <option value="BANK_STATEMENT">Bank Statements</option>
              <option value="AR_INVOICE">AR Invoices</option>
              <option value="CUSTOMER_PAYMENT">Customer Payments</option>
              <option value="IC_TRANSACTION">IC Transactions</option>
              <option value="BUDGET">Budgets</option>
              <option value="CASH_FORECAST">Cash Forecasts</option>
              <option value="VOLUME_FORECAST">Volume Forecasts</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>ERP Type</label>
            <select value={erpType} onChange={e => setErpType(e.target.value)} style={selectStyle}>
              <option value="AUTO">Auto-detect</option>
              <option value="SAP">SAP</option>
              <option value="ORACLE">Oracle</option>
              <option value="NETSUITE">NetSuite</option>
              <option value="GENERIC">Generic</option>
            </select>
          </div>
        </div>

        {/* Drop zone */}
        <div
          onDragEnter={e => handleDrag(e, setDataDragOver, dataDragOver)}
          onDragOver={e => handleDrag(e, setDataDragOver, dataDragOver)}
          onDragLeave={e => handleDrag(e, setDataDragOver, dataDragOver)}
          onDrop={e => handleDrop(e, setDataFile, setDataDragOver)}
          onClick={() => dataInputRef.current?.click()}
          style={{
            border: `2px dashed ${dataDragOver ? 'var(--accent)' : 'var(--border-subtle)'}`,
            borderRadius: 8, padding: '32px 20px', textAlign: 'center', cursor: 'pointer',
            background: dataDragOver ? 'var(--accent-subtle)' : 'transparent',
            transition: 'all 0.15s ease',
            marginBottom: 16,
          }}
        >
          <input
            ref={dataInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={e => setDataFile(e.target.files?.[0] || null)}
            style={{ display: 'none' }}
          />
          {dataFile ? (
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{dataFile.name}</div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
                {(dataFile.size / 1024).toFixed(1)} KB
                <span style={{ marginLeft: 8, cursor: 'pointer', color: '#ef4444' }}
                  onClick={e => { e.stopPropagation(); setDataFile(null); }}>
                  Remove
                </span>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                Drag & drop data file (CSV or Excel)
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 4 }}>
                AP invoices, POs, vendors, customers, bank statements, assets, AR, etc.
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <ActionButton
            label={dataUploading ? 'Uploading...' : 'Upload & Process'}
            onClick={() => handleDataUpload()}
            variant="primary"
            disabled={!dataFile || dataUploading}
          />
        </div>
      </div>

      {/* Active processing indicator */}
      {activeDataUploadId && (
        <div style={{
          background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.2)',
          borderRadius: 8, padding: 16, marginBottom: 20, fontSize: 12, color: 'var(--navy-700)',
        }}>
          Processing upload #{activeDataUploadId}...
        </div>
      )}

      {/* Info banner */}
      <div style={{
        background: 'rgba(6,182,212,0.06)', border: '1px solid var(--accent-border)',
        borderRadius: 8, padding: 14, marginBottom: 20, fontSize: 11, color: 'var(--text-secondary)',
      }}>
        Upload operational data files for ingestion into canonical tables.
        The connector framework auto-detects data type and ERP format from file headers,
        maps columns using pre-configured profiles, validates data, and ingests into the appropriate tables.
      </div>

      {/* Upload History */}
      <div style={{ marginTop: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12 }}>Data Upload History</div>
        <DataTable
          columns={historyColumns}
          rows={dataUploads}
          loading={dataUploadsLoading}
          emptyMessage="No data uploads yet. Upload a file above to get started."
          rowKey="id"
        />
      </div>
    </>
  );
}
